import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'

import Image from 'next/image'
import { useRouter } from 'next/router'
import {
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  Hidden,
  Link,
  Button,
  AccordionDetails,
  colors,
} from '@material-ui/core'
import ArrowDropDownRoundedIcon from '@material-ui/icons/ArrowDropDownRounded'
import TrendingUpRounded from '@material-ui/icons/TrendingUpRounded'

import { useFiltersContext } from '../../../../contexts/Filters'

// types
import { SavingAccount } from '../../../../../types/savingAccount'
import { Cryptocurrency, Fiat } from '../../../../../types/currency'

// utils
import { round } from '../../../../utils/number'
import { urls } from '../../../../utils/config'

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: '12px',
    padding: '0',
    alignItems: 'center',
    border: 0,
    [theme.breakpoints.up('md')]: {
      padding: '1rem 0',
    },
  },
  imageWrapper: {
    padding: '.4rem',
    background: 'white',
    borderRadius: '.6rem',
    width: '2.8rem',
    height: '2.8rem',
    margin: '.8rem 0 .4rem 0',
    [theme.breakpoints.up('md')]: {
      margin: '0 .4rem',
    },
  },
  imageContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
  },
  detailsContainer: {
    width: '100%',
    textAlign: 'center',
    '& > p': {
      margin: '.4rem 0',
      fontSize: '.8rem',
      [theme.breakpoints.up('md')]: {},
    },
    [theme.breakpoints.up('md')]: {
      textAlign: 'left',
      width: '60%',
    },
  },
  accordionSummary: {
    padding: '1rem 2rem',
    width: '100%',
    borderRadius: '1rem',
    border: '1px solid rgba(0, 0, 0, 0) !important',
    '&.expanded': {
      border: '1px solid rgba(0, 0, 0, 0.05) !important',
      '& $accordionSummaryContent': {
        margin: '0 !important',
      },
    },
    '& p': {
      fontSize: '1rem',
      fontWeight: '600',
      color: 'rgba(43, 24, 65, .8)',
    },
    '&.odd': {
      background: 'rgba(0, 0, 0, 0.04)',
    },
  },
  accordionSummaryContent: {
    margin: 0,
  },
  securityScore: {
    textAlign: 'center',
  },
  price: {},
  feesContainer: {},
  starting: {},
  dropDownIconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& > svg': {
      transform: 'rotateZ(0deg)',
      transition: 'transform 0.2s ease-in-out',
    },
    '&.expanded > svg': {
      transform: 'rotateZ(-180deg)',
      transition: 'transform 0.2s ease-in-out',
    },
  },
  number: {
    [theme.breakpoints.up('md')]: {
      // margin: '0 1rem',
    },
  },
  title: {
    textOverflow: 'ellipsis',
  },
  numberContainer: {},
  imageGrid: {
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  accordionDetailsContainer: {
    marginTop: '.2rem',
    padding: '4rem',
  },
  accordionDetailsContent: {
    flexDirection: 'column',
    alignItems: 'center',
    '& button': {
      margin: '1rem 1rem',
    },
    '& button p': {
      color: 'white',
      fontSize: '.8rem',
    },
    '& a': {
      color: 'black',
      fontSize: '.8rem',
      borderBottom: '1px solid',
      paddingBottom: '.4rem',
      margin: '1rem 1rem',
      width: 'fit-content',
    },
  },
  descriptionContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    '& h6': {
      fontFamily: theme.typography.h1.fontFamily,
      fontSize: '1rem',
    },
    '& p': {
      fontWeight: '400',
      textAlign: 'left',
    },
  },
  ctaContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '1rem',
    justifyContent: 'flex-end',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  button: {
    borderRadius: '.8rem',
  },
  reviewLink: {
    fontWeight: 600,
  },
}))

interface props {
  account: SavingAccount
  depositCryptocurrencies: Cryptocurrency[]
  fiat?: Fiat
  number?: number
}

function SavingsAccountsCard({
  account: {
    path_id,
    interestRate,
    url,
    name,
    icon,
    kyc,
    payout,
    term,
    minimum_deposit,
    maximum_deposit,
    countries,
    review_url,
  },
  number,
}: props): JSX.Element {
  const classes = useStyles({})

  const [expanded, setExpanded] = React.useState(false)

  const handleChange = () => {
    setExpanded(!expanded)
  }

  const filtersContext = useFiltersContext()
  const [estimatedEarnings, setEstimatedEarnings] = useState<number>(
    filtersContext.filters.depositInput || 0,
  )
  const rate = interestRate?.rate ? round(interestRate.rate * 100, 2) : null

  const goToSite = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    window.open(url)
  }

  useEffect(() => {
    let newEstimatedEarnings
    if (filtersContext.filters.depositInput && rate && filtersContext.filters.depositPeriod) {
      newEstimatedEarnings =
        filtersContext.filters.depositInput *
        Math.pow(1 + rate / 100, parseInt(filtersContext.filters.depositPeriod))
      setEstimatedEarnings(round(newEstimatedEarnings - filtersContext.filters.depositInput, 2))
    }
  }, [filtersContext.filters.depositInput, filtersContext.filters.depositPeriod])

  const { push } = useRouter()

  return (
    <Accordion className={classes.root} onChange={handleChange}>
      <AccordionSummary
        classes={{
          root: `
          ${classes.accordionSummary} 
          ${number && number % 2 === 0 ? 'even' : 'odd'} 
          ${expanded ? 'expanded' : ''}`,
          content: classes.accordionSummaryContent,
        }}
      >
        <Grid container alignItems="center" spacing={6}>
          <Grid item container alignItems="center" xs={3}>
            <Grid
              container
              item
              xs={12}
              md={3}
              alignItems="center"
              className={classes.numberContainer}
              justify="flex-start"
            >
              <div className={`${classes.dropDownIconContainer} ${expanded ? 'expanded' : ''}`}>
                <ArrowDropDownRoundedIcon />
              </div>
              <Typography className={classes.number} variant="body2">
                {number && `${number}.`}
              </Typography>
            </Grid>
            <Grid
              item
              container
              xs={12}
              md={9}
              alignItems="center"
              justify="flex-start"
              className={classes.imageGrid}
            >
              <div className={classes.imageWrapper}>
                <div className={classes.imageContainer}>
                  <Image
                    src={icon ? icon.url : '/svg/wallet.svg'}
                    objectFit="contain"
                    layout="fill"
                  />
                </div>
              </div>
              <Typography className={classes.title} variant="body2">
                {name}
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={2}>
            <Typography className={classes.price} variant="body2">
              {payout}
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Typography className={classes.starting} variant="body2">
              {kyc ? 'Yes' : 'No'}
            </Typography>
          </Grid>

          <Grid item xs={2}>
            {term}
          </Grid>
          <Grid container alignItems="center" item xs={2} className={classes.feesContainer}>
            <TrendingUpRounded style={{ color: colors.green[400] }} /> {rate}%
          </Grid>
          <Grid container alignItems="center" item xs={2} className={classes.feesContainer}>
            {`$${estimatedEarnings}`}
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails
        classes={{
          root: `
          ${classes.accordionSummary} 
          ${number && number % 2 === 0 ? 'even' : 'odd'} 
          ${expanded ? 'expanded' : ''} 
          ${classes.accordionDetailsContainer}
          `,
        }}
      >
        <Grid container spacing={2} justify="center" className={classes.accordionDetailsContent}>
          <div className={classes.descriptionContainer}>
            <div>
              <Typography variant="h6">Minimum Deposit</Typography>
              <Typography variant="body2">{minimum_deposit}</Typography>
            </div>
            <div>
              <Typography variant="h6">Maximum Deposit</Typography>
              <Typography variant="body2">{maximum_deposit}</Typography>
            </div>
            <div>
              <Typography variant="h6">Countries</Typography>
              <Typography variant="body2">{countries}</Typography>
            </div>
          </div>
          <div className={classes.ctaContainer}>
            <Link
              underline="none"
              href={`${urls.savingAccounts}/${path_id}`}
              className={classes.reviewLink}
            >{`Read Review`}</Link>
            <Button
              onClick={goToSite}
              variant="contained"
              color="primary"
              className={classes.button}
              disableElevation
            >
              <Typography variant="body2">{`Try ${name} now!`}</Typography>
            </Button>
          </div>
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

export default SavingsAccountsCard
